/**
 * SF-4847: shown instead of the Approve panel while the person has taken items out of the plan.
 * A changed plan is never built: "Make version N" sends the removals (and anything typed in the
 * chat box) to Eva, who redraws the whole plan without them; that new version can be approved.
 */
import { Alert, Button, Card, Popconfirm, Space, Typography } from 'antd';
import { PLAN_HAS_REMOVED_ITEMS_TEXT } from '../api/demoBuildsClient';

export interface MakeVersionPanelProps {
  /** The version on screen; the button makes the next one. */
  version: number;
  count: number;
  /** Sending is allowed now (ready for review and no other message on its way). */
  canSend: boolean;
  sending: boolean;
  error: string | null;
  /** S6: Eva's last version still had items taken out earlier (they count in `count`). */
  stale?: boolean;
  onMake: () => void;
  /** Cancel stays on offer while items are taken out (the Approve panel it replaces held it). */
  canCancel: boolean;
  cancelling: boolean;
  onCancel: () => void;
  /** A failed cancel (or approve) shown here, since this panel hides the Approve panel that showed it. */
  actionError?: React.ReactNode;
}

export const MakeVersionPanel: React.FC<MakeVersionPanelProps> = ({
  version,
  count,
  canSend,
  sending,
  error,
  stale = false,
  onMake,
  canCancel,
  cancelling,
  onCancel,
  actionError = null,
}) => (
  <Card size="small" title="Make a new version" data-testid="make-version-panel">
    <Space direction="vertical" style={{ width: '100%' }}>
      {stale && (
        <Typography.Text strong data-testid="make-version-stale">
          {PLAN_HAS_REMOVED_ITEMS_TEXT}
        </Typography.Text>
      )}
      <Typography.Text>
        You took out {count} {count === 1 ? 'item' : 'items'}. Eva redraws the plan without{' '}
        {count === 1 ? 'it' : 'them'}, and you can build once you have checked the new version.
        Anything typed in the chat box goes with it.
      </Typography.Text>
      {error && <Alert type="error" showIcon message={error} data-testid="make-version-error" />}
      {actionError}
      <Space>
        <Button
          type="primary"
          disabled={!canSend || cancelling}
          aria-busy={sending}
          loading={sending}
          onClick={onMake}
        >
          Make version {version + 1}
        </Button>
        <Popconfirm
          title="Cancel this demo?"
          description="The build stops here and nothing is made."
          okText="Yes, cancel it"
          cancelText="Keep it"
          onConfirm={onCancel}
          disabled={!canCancel || sending}
        >
          <Button disabled={!canCancel || sending} loading={cancelling}>
            Cancel demo
          </Button>
        </Popconfirm>
      </Space>
    </Space>
  </Card>
);
